import React, { SetStateAction, useEffect, useState } from "react";
import TextBox from "../Common/TextBox";
import MtiButton from "../Buttons/MtiButton";
import UserApi from "../../api/UserApi";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { saveUserCredential } from "../../redux/actions/LocalUserActions";
import IconApp from "../../Icons/AppIcon";
import IconShowPass from "../../Icons/ShowPassword";
import IconHidePass from "../../Icons/HidePassword";
import IconInfo from "../../Icons/NotificationMessage/Info";
import {
  removeNotification,
  sendNotification,
} from "../../redux/actions/NotificationAction";
import "./Login.css";
import { ENotificationType } from "../../Model/ENotificationType";
import SuiteApi from "../../api/SuiteApi";
import Modal from "../Common/Modal";
import WarningView from "../../WarningView";
import { User, UserCredential } from "../../Model/User";
import { ApplicationInfo } from "../../Model/ApplicationInfo";

type Props = {
  setShowLoading: React.Dispatch<SetStateAction<boolean>>;
  setShowErrorNotification: React.Dispatch<SetStateAction<boolean>>;
};

export default function LoginForm({
  setShowLoading,
  setShowErrorNotification,
}: Props) {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthFail, setIsAuthFail] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [instrumentTypeName, setInstrumentTypeName] = useState("ROCK IMAGER");
  const [usersLoggedIn, setUsersLoggedIn] = useState([] as User[]);
  const [userCredential, setUserCredential] = useState({} as UserCredential);
  const [applicationInfo, setApplicationInfo] = useState({} as ApplicationInfo);
  const getUsersLoggedIn = () => {
    setShowLoading(true);
    UserApi.getUsersLoggedIn()
      .then((result) => {
        setUsersLoggedIn(result);
      })
      .catch((error) => {
        dispatch(sendNotification(ENotificationType.Error, error.message));
        setShowErrorNotification(true);
        setShowLoading(false);
      });
  };

  const authenticate = () => {
    setShowLoading(true);
    UserApi.authenticate(userName, password)
      .then((result) => {
        setUserCredential(result);
        setShowLoading(false);
      })
      .catch((error) => {
        setIsAuthFail(true);
        // if (error.message !== "Invalid user name or password") {
        //   dispatch(sendNotification(ENotificationType.Error, error.message));
        //   setShowErrorNotification(true);
        // } else {
        //   dispatch(removeNotification());
        // }
        dispatch(removeNotification());
        setShowLoading(false);
      });
  };

  const login = () => {
    UserApi.login(userCredential)
      .then((result) => {
        dispatch(saveUserCredential(userCredential));
        history.push("/");
        setShowLoading(false);
      })
      .catch((error) => {
        // dispatch(sendNotification(ENotificationType.Error, error.message));
        setShowErrorNotification(true);
        setShowLoading(false);
      });
  };

  const isCurrentUserLoggedIn = () => {
    const userNameList = usersLoggedIn.map((user) => {
      return user.userName;
    });
    return userNameList.includes(userCredential.userName);
  };

  const onEnterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      authenticate();
    }
  };

  const toggleShowPass = () => {
    setShowPassword(!showPassword);
  };

  const resetFailStatus = () => {
    dispatch(removeNotification());
    setIsAuthFail(false);
  };

  const forgotPassword = () => {
    history.push("/forgot-password");
  };

  const resetPassword = () => {
    history.push("/reset-password");
  };

  useEffect(() => {
    SuiteApi.getInstrumentTypeName().then((value) => {
      setInstrumentTypeName(value);
    });
    SuiteApi.getApplicationInfo().then((value) => {
      setApplicationInfo(value);
    });
  }, []);

  useEffect(() => {
    if (
      userCredential.userName !== undefined &&
      (usersLoggedIn.length === 0 || isCurrentUserLoggedIn())
    ) {
      login();
    }
  }, [userCredential, usersLoggedIn]);

  return (
    <>
      <div className="login-form">
        <div className="login-title">
          <div className="title-container">
            <IconApp width="34" height="34" viewBox="0 0 48 48" fill="none" />
            <p>
              {instrumentTypeName}
              <sup>®</sup>
            </p>
          </div>
        </div>
        <TextBox
          name="username"
          value={userName}
          placeHolder="Username"
          onKeyDown={(event) => setUserName(event.currentTarget.value)}
          onChange={(value) => setUserName(value as string)}
          onClick={resetFailStatus}
          onKeyPress={onEnterKeyPress}
          supportAutoFill={true}
          style={{
            border: !isAuthFail ? "1px inset #828282" : "1px solid #C92932",
          }}
        />
        {isAuthFail && (
          <div className="login-fail">
            <IconInfo fill="#C92932" width="10" height="10" />
            <p>Please Enter a valid username</p>
          </div>
        )}
        <div className="login-password">
          <TextBox
            name="password"
            variant={showPassword ? "text" : "password"}
            placeHolder="Password"
            value={password}
            onKeyDown={(event) => setPassword(event.currentTarget.value)}
            onChange={(value) => setPassword(value as string)}
            onClick={resetFailStatus}
            onKeyPress={onEnterKeyPress}
            supportAutoFill={true}
            style={{
              border: !isAuthFail ? "1px inset #828282" : "1px solid #C92932",
            }}
          />
          <button onClick={toggleShowPass} className="login-password-button">
            {showPassword && <IconShowPass width="17" height="17" />}
            {!showPassword && <IconHidePass width="17" height="17" />}
          </button>
        </div>
        {isAuthFail && (
          <div className="login-fail">
            <IconInfo fill="#C92932" width="10" height="10" />
            <p>Please Enter a valid password</p>
          </div>
        )}
        <div className="login-buttons">
          <MtiButton
            size="large"
            onClick={() => {
              getUsersLoggedIn();
              authenticate();
            }}
            style={{ width: "100%", margin: 0 }}
            disabled={
              userName.trim().length === 0 || password.trim().length === 0
            }
          >
            LOGIN
          </MtiButton>
        </div>
        <div className="login-form-footer">
          <div className="login-footer-button" onClick={resetPassword}>
            Input OTP
          </div>
          <div className="login-footer-button" onClick={forgotPassword}>
            Forgot Password
          </div>
        </div>
      </div>
      <div className="login-footer">
        <div>
          <p>{"version " + applicationInfo.productVersion}</p>
        </div>
        <div>
          <p>{applicationInfo.companyName}</p>
        </div>
        <div>
          <p>{`©${applicationInfo.copyrightYear}`}</p>
        </div>
      </div>
      <Modal
        show={
          userCredential.userName !== undefined &&
          usersLoggedIn.length > 0 &&
          !isCurrentUserLoggedIn()
        }
        hideOnBackdropClick={false}
        onHide={() => {
          setUsersLoggedIn([] as User[]);
          setUserCredential({} as UserCredential);
        }}
      >
        <Modal.Body style={{ textAlign: "left" }}>
          <WarningView
            title="Login Conflict"
            message="Another user is currently logged in. Are you sure you want to continue?"
            instruction="If you proceed with the action, the other user will be forced to log out."
            onClickOk={login}
            onClickCancel={() => {
              setUsersLoggedIn([] as User[]);
              setUserCredential({} as UserCredential);
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
